import { useTranslation } from 'react-i18next';
import { useNavigate, useParams, Link } from 'react-router-dom';
import { useQuery } from '@apollo/client';
import { useConfirm } from 'material-ui-confirm';
import { useSnackbar } from 'notistack';
import { Box, Button, Card, Chip, Container, Stack, Typography, styled } from '@mui/material';

import useResponsive from '../hooks/useResponsive';
import Page from '../components/Page';
import BasicBreadcrumb from '../components/BasicBreadcrumb';
import LoadingInfo from '../sections/people/LoadingInfo';
import KeyValueDisplay from '../components/KeyValueDisplay';
import KeyValueLabelDisplay from '../components/KeyValueLabelDisplay';
import { PushNotificationGQLFunction } from '../graphql/PUSH_NOTIFICATION';

const RootStyle = styled('div')(() => ({
  display: 'flex',
  justifyContent: 'center'
}));
const ContentStyle = styled(Card)(({ theme }) => ({
  width: '100%',
  margin: theme.spacing(2),
  display: 'flex',
  justifyContent: 'flex-end',
  flexDirection: 'column',
  padding: theme.spacing(2)
}));

function DataDisplay({ data, smUp, t }) {
  return (
    <Box
      sx={{
        display: 'grid',
        columnGap: 2,
        rowGap: 3,
        gridTemplateColumns: smUp ? 'repeat(2, 1fr)' : '1fr',
        m: 1,
        mb: 4
      }}
    >
      <KeyValueDisplay title={t('common.id')} data={data.adminInfo.id} />
      <KeyValueDisplay title={t('common.username')} data={data.adminInfo.username} />
      <KeyValueDisplay title={t('common.email')} data={data.adminInfo.email} />
      <KeyValueDisplay title={t('common.telephone')} data={data.adminInfo.telephone || '-'} />
      <KeyValueLabelDisplay
        title={t('common.status')}
        data={t(`common.${data.adminInfo.userState ? 'blocked' : 'normal'}`)}
        color={!data.adminInfo.userState ? 'success' : 'warning'}
      />
    </Box>
  );
}

export default function PushNotificationInfo() {
  const { t } = useTranslation();
  const smUp = useResponsive('up', 'sm');
  const { pushNotificationId } = useParams();
  const navigate = useNavigate();
  const confirm = useConfirm();
  const { enqueueSnackbar } = useSnackbar();

  // query
  const { loading, data } = useQuery(PushNotificationGQLFunction.GET_PUSH_NOTIFICATION_INFO, {
    variables: { id: pushNotificationId }
  });

  const bread = [
    <Link href="/push-notification" underline="hover" key="1" color="inherit">
      {t('notify.title')}
    </Link>,
    <Typography key="2" color="text.primary">
      {t('notify.info_title')}
    </Typography>
  ];

  return (
    <Page title={t('notify.info_title')}>
      <Container maxWidth={false}>
        <Stack direction="column">
          <Typography variant="h4" gutterBottom>
            {t('notify.info_title')}
          </Typography>
          <BasicBreadcrumb bread={bread} />
        </Stack>

        <RootStyle>
          <ContentStyle>
            {/* {!queryLoading && queryData ? <DataDisplay data={queryData} smUp={smUp} t={t} /> : <LoadingInfo smUp={smUp} />} */}
            <Stack direction="row" spacing={1} mb={'1rem'} justifyContent="flex-start" alignItems="baseline">
              <Typography variant="body2" minWidth={'2rem'}>
                {t('notify.to')} :
              </Typography>
              <Box
                style={{
                  display: 'flex',
                  flexDirection: 'row',
                  alignItems: 'center',
                  flexWrap: 'wrap',
                  gap: '0.5rem 0.5rem'
                }}
              >
                {data?.pushNotification.users.map((user, index) => (
                  <Chip
                    variant="outlined"
                    size="small"
                    label={user.username}
                    key={index}
                    sx={{ maxWidth: '150px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}
                  />
                ))}
              </Box>
            </Stack>
            <Typography variant="caption" display="block" gutterBottom>
              {data?.pushNotification.timeSend}
            </Typography>
            <Typography variant="subtitle1" gutterBottom>
              {data?.pushNotification.title}
            </Typography>
            <Typography variant="body1" gutterBottom>
              {data?.pushNotification.content}
            </Typography>
          </ContentStyle>
        </RootStyle>
      </Container>
    </Page>
  );
}
